"use client";

import React, { useState, useEffect, useMemo } from "react";
import {
  SearchIcon,
  Home,
  GraduationCap,
  BookOpen,
  Newspaper,
  Briefcase,
  Calendar,
  HelpCircle,
  ExternalLink,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import Link from "next/link";
import { QUICK_LINKS } from "./links";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/fetcher";
import { LinearIndeterminate } from "./LinearIndeterminate";
import { cn } from "@/lib/utils";

// Types for API response
interface SearchResult {
  id: string;
  title: string;
  name: string;
  description?: string;
  thumbnail?: string;
}

interface SearchApiResponse {
  success: boolean;
  message: string;
  data: {
    careers?: SearchResult[];
    events?: SearchResult[];
    stories?: SearchResult[];
    opportunities?: SearchResult[];
    institutions?: SearchResult[];
    [key: string]: SearchResult[] | undefined;
  };
  status: number;
}

const ICONS: Record<string, any> = {
  careers: GraduationCap,
  events: Calendar,
  stories: Newspaper,
  opportunities: Briefcase,
  institutions: BookOpen,
};

export function Search({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Always show static quick links as fallback

  // Fetch search results from API
  const { data, isFetching } = useQuery<SearchApiResponse>({
    queryKey: ["global-search", search],
    queryFn: async () => {
      const res = await api.get(`/search/global?query=${encodeURIComponent(search)}`);
      return res.data;
    },
    enabled: !!search,
    staleTime: 0,
  });

  // Group API results by type
  const dynamicGroups = useMemo(() => {
    if (!data?.data) return [];
    // console.log('data ==>', data.data);
    
    const groups = Object.entries(data.data)
      .filter(([_, items]) => items && items.length > 0)
      .map(([key, items]) => ({
        groupKey: key,
        groupValue: key.charAt(0).toUpperCase() + key.slice(1),
        children: (items as SearchResult[]).map((item) => ({
          label: item.title ?? item.name,
          url: `/${key}/${item.id}`,
          icon: ICONS[key] || SearchIcon,
          meta: item.description??"",
          target: "_self",
        })),
      }));
      // console.log('groups ==>', groups);
      return groups;
  }, [data]);

  // Combine dynamic groups and static quick links
  const allGroups = useMemo(() => {
    if (search && dynamicGroups.length > 0) {
      return [...dynamicGroups, ...QUICK_LINKS];
    }
    return QUICK_LINKS;
  }, [search, dynamicGroups, QUICK_LINKS]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // console.log('allGroups ==>', allGroups);
  
  return (
    <>
      <div
        onClick={() => setOpen(true)}
        className={cn("flex items-center justify-around gap-4 rounded-md border border-input px-3 py-2 text-sm text-muted-foreground ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 hover:bg-muted", className)}
      >
        <span className="flex items-center gap-1 truncate">
          <SearchIcon className="h-4 w-4" />
          <span className="hidden xs:block">Search healthcare careers...</span>
          <span className="2xs:block hidden xs:hidden">Search</span>
        </span>
        <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100 md:inline-flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </div>
      <CommandDialog open={open} onOpenChange={setOpen} className="w-full sm:max-w-xl lg:max-w-3xl">
        <CommandInput
          placeholder="Search careers, resources, events..."
          value={search}
          onValueChange={setSearch}
        />
        {isFetching && (
          <LinearIndeterminate />
        )}
        <CommandList>
          <CommandEmpty>{isFetching ? 'Searching...' : 'No results found.'}</CommandEmpty>
          {allGroups.map((doc) => (
            <CommandGroup key={doc.groupKey} heading={doc.groupValue}>
              {doc.children.map((d, i) => {
                const IconComponent = d.icon;
                return (
                  <Link
                    href={d.url}
                    key={d.label + i}
                    onClick={() => setOpen(false)}
                    target={d.target}
                  >
                    <CommandItem className="cursor-pointer" value={d.label + d.meta}>
                      <IconComponent className="mr-2 h-4 w-4" />
                      <span>{d.label}</span>
                    </CommandItem>
                  </Link>
                );
              })}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
